import { useEffect, useMemo } from "react";
import _ from "lodash";

import PusherManager from "utils/pusher-manager";

import { useSignedUser } from "entities/Auth/sdk";
import { useDealTeamMembers } from "entities/Team/sdk";

export const useLoggedUserIsDealAdmin = ({ dealId }: { dealId: number }) => {
  const { data: dealTeamMembers } = useDealTeamMembers({ dealId });
  const { data: signedUser } = useSignedUser();

  const loggedUserIsDealAdmin = useMemo(() => {
    const matchedUser = _.find(
      dealTeamMembers,
      (teamMember) =>
        teamMember.user.email.toLowerCase() === signedUser?.email.toLowerCase()
    );
    return matchedUser?.is_admin || false;
  }, [signedUser, dealTeamMembers]);

  return { loggedUserIsDealAdmin };
};

export const useDealPropertyUpdate = ({
  updateEventHandler,
}: {
  updateEventHandler: ({ dealId }: { dealId: number }) => void;
}) => {
  const { data: user } = useSignedUser();

  useEffect(() => {
    if (!_.isUndefined(user)) {
      return PusherManager.bindToDealPropertyUpdateEvent({
        userId: user.id,
        eventHandler: ({ deal_id }) => updateEventHandler({ dealId: deal_id }),
      });
    }
  }, [user, updateEventHandler]);
};

export const useProformaPDFGenerationReadyEvent = ({
  successEventHandler,
  failureEventHandler,
}: {
  successEventHandler: () => void;
  failureEventHandler: () => void;
}) => {
  const { data: user } = useSignedUser();

  useEffect(() => {
    if (!_.isUndefined(user)) {
      return PusherManager.bindToProformaPDFGenerationSucceededEvent({
        userId: user.id,
        eventHandler: successEventHandler,
      });
    }
  }, [user, successEventHandler]);

  useEffect(() => {
    if (!_.isUndefined(user)) {
      return PusherManager.bindToProformaPDFGenerationFailedEvent({
        userId: user.id,
        eventHandler: failureEventHandler,
      });
    }
  }, [user, failureEventHandler]);
};

export const useMemoPDFGenerationReadyEvent = ({
  successEventHandler,
  failureEventHandler,
}: {
  successEventHandler?: ({
    dealId,
    memoId,
    memoPDFId,
    disableGlobalNotification,
  }: {
    dealId: number;
    memoId: number;
    memoPDFId: number;
    disableGlobalNotification: boolean;
  }) => void;
  failureEventHandler?: ({ detail }: { detail: string }) => void;
}) => {
  const { data: user } = useSignedUser();

  useEffect(() => {
    if (!_.isUndefined(user) && !_.isNil(successEventHandler)) {
      return PusherManager.bindToMemoPDFGenerationSucceededEvent({
        userId: user.id,
        eventHandler: ({ data }) =>
          successEventHandler({
            dealId: data.deal_id,
            memoId: data.memo_id,
            memoPDFId: data.memo_pdf_id,
            disableGlobalNotification: data.is_created_through_e_sign_flow,
          }),
      });
    }
  }, [user, successEventHandler]);

  useEffect(() => {
    if (!_.isUndefined(user) && !_.isNil(failureEventHandler)) {
      return PusherManager.bindToMemoPDFGenerationFailedEvent({
        userId: user.id,
        eventHandler: failureEventHandler,
      });
    }
  }, [user, failureEventHandler]);
};
